<template>
    <v-container class="pa-5" fluid>
        <ConfirmDialog
            :data="currentAttachment"
            :is-open="deleteAttachmentDialog"
            :action-loading="attachmentLoadingOnDelete"
            @on-confirm="deleteAttachment"
            @on-abort="closeDeleteAttachmentDialog"
        >
            <template #title>
                Usuwanie załącznika <strong>{{ formatName(currentAttachment.file) }}</strong>
            </template>
            <template #content>
                Czy na pewno chcesz usunąć załącznik?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>
        <page-title>
            {{ pageTitle }}
        </page-title>

        <v-row>
            <v-col cols="12" xl="8" offset-xl="2">
                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <v-card>
                                <v-card-title>
                                    <v-col cols="12">
                                        <h2 v-if="actionName === 'updateProcessEntry'" class="text-h6 mb-2">
                                            <v-text-field
                                                v-model="processEntry.name"
                                                label="Tytuł wpisu"
                                            />
                                        </h2>
                                        <h2 v-else class="text-h6 mb-2">
                                            {{ processEntry.name }}
                                        </h2>
                                    </v-col>
                                </v-card-title>

                                <v-card-text>
                                    <v-container class="pa-0 ml-2">
                                        <v-row>
                                            <v-col cols="12">
                                                <v-textarea v-if="actionName === 'updateProcessEntry'" v-model="processEntry.description" label="Treść wpisu" />
                                                <p v-else>
                                                    {{ processEntry.description }}
                                                </p>
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </v-card-text>
                            </v-card>
                        </v-col>
                        <v-col cols="12">
                            <v-card>
                                <v-card-text>
                                    <v-file-input
                                        v-if="actionName === 'updateProcessEntry'"
                                        v-model="items"
                                        accept="image/*, .pdf"
                                        counter
                                        multiple
                                        placeholder="Wybierz pliki"
                                        prepend-icon="mdi-paperclip"
                                        outlined
                                        dense
                                        :show-size="1000"
                                    >
                                        <template #selection="{text}">
                                            <v-chip
                                                label
                                                small
                                                close
                                                @click:close="removeAttachment()"
                                            >
                                                {{ text }}
                                            </v-chip>
                                        </template>
                                    </v-file-input>
                                    <v-data-table
                                        :items="uploadedFiles"
                                        hide-default-header
                                        hide-default-footer
                                        disable-pagination
                                        :mobile-breakpoint="0"
                                        name="complaintAttachments"
                                    >
                                        <template #item="{item}">
                                            <tr>
                                                <td>
                                                    <span>
                                                        {{ formatName(item.file) }}
                                                    </span>
                                                    <div class="d-inline-flex">
                                                        <v-tooltip top :open-delay="tooltipDelay">
                                                            <template #activator="{on}">
                                                                <v-btn
                                                                    icon
                                                                    data-sentry-label="Pobierz"
                                                                    v-on="on"
                                                                    @click="openInNewTab(item.file)"
                                                                >
                                                                    <v-icon color="grey">
                                                                        mdi-download
                                                                    </v-icon>
                                                                </v-btn>
                                                            </template>
                                                            <span>Pobierz</span>
                                                        </v-tooltip>
                                                        <v-tooltip v-if="actionName === 'updateProcessEntry'" top :open-delay="tooltipDelay">
                                                            <template #activator="{on}">
                                                                <v-btn
                                                                    icon
                                                                    data-sentry-label="Usuń"
                                                                    v-on="on"
                                                                    @click="showDeleteAttachmentDialog(item)"
                                                                >
                                                                    <v-icon color="pink">
                                                                        mdi-delete
                                                                    </v-icon>
                                                                </v-btn>
                                                            </template>
                                                            <span>Usuń</span>
                                                        </v-tooltip>
                                                    </div>
                                                </td>
                                            </tr>
                                        </template>
                                    </v-data-table>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>

                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <v-btn
                                color="error"
                                medium
                                class="mr-3"
                                data-sentry-label="Wstecz"
                                :disabled="actionLoading"
                                @click="onClose"
                            >
                                Wstecz
                            </v-btn>
                            <v-btn
                                v-if="actionName === 'updateProcessEntry'"
                                color="success"
                                medium
                                data-sentry-label="Zapisz"
                                :disabled="actionLoading"
                                @click="saveProcessSEntry"
                            >
                                Zapisz
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { apiConfig } from '@/config'
import { errorHandler } from '@/utils/errorHandler'
import store from '@/./store'
import PageTitle from '@/components/common/PageTitle'
import dateFormatter from '@/mixins/dateFormatter'
import attachment from '@/mixins/attachment'
import tooltipDelay from '@/mixins/tooltip'
import router from '@/router'
import ConfirmDialog from '@/components/common/ConfirmDialog'

export default {
    name: 'Entry',
    components: {
        PageTitle, ConfirmDialog
    },
    mixins: [ dateFormatter, tooltipDelay, attachment ],
    beforeRouteEnter(to, from, next) {
        store.dispatch('wiki/getProcessEntry', to.params.id)
            .then(response => {
                next(vm => {
                    vm.processEntry = Object.assign({}, response.data)
                    vm.actionName = to.params.actionName
                    vm.ancestorsNames = to.params.ancestorsNames
                    const ancestorsPath = vm.getAncestorsNamesPath(vm.ancestorsNames)

                    function formatName(processEntryName) {
                        if (/\s/.test(processEntryName)) {
                            let processEntryNameToList = processEntryName.substring(0, 31).split(' ')

                            processEntryNameToList.pop()
                            let name = processEntryNameToList.join(' ')

                            return name + '...'
                        }

                        return vm.processEntry.name + '...'
                    }
                    vm.pageTitle = `Wiki / ${ancestorsPath} / ${vm.processEntry.name.length > 30 ? formatName(vm.processEntry.name): vm.processEntry.name}`
                })
            })
            .catch(error => {
                next(error)
                errorHandler(error)
            })
    },
    data() {
        return {
            actionName: null,
            processEntry: {},
            currentAttachment: {},
            pageTitle: null,
            uploadedFiles: [],
            attachmentError: [],
            items: [],
            actionLoading: false,
            deleteAttachmentDialog: false,
            attachmentLoadingOnDelete: false,
            ancestorsNames: []

        }
    },
    created() {
        this.createUploadedUrls()
    },
    methods: {
        onClose() {
            router.push({ name: 'wikiWiki', params: {
                returnToSubjectId: this.processEntry.processSubjectId, 
                tabsTitles: this.ancestorsNames 
            }})
                .catch(error => {
                    errorHandler(error)
                })
        },
        saveProcessSubject() {
            return true
        },
        createUploadedUrls() {
            const mediaRoot = apiConfig.FACTORYBASE_API_URI

            this.uploadedFiles = []
            this.$store.state.wiki.wiki.processEntryAttachments.forEach(function(item) {
                let file = {
                    id : item.id,
                    file: mediaRoot + item.file
                }

                this.uploadedFiles.push(file)
            }, this)
        },
        deleteAttachment(attachment) {
            this.uploadedFiles.splice(this.uploadedFiles.indexOf(attachment), 1)
            this.$store.dispatch('wiki/deleteAttachment', attachment.id)
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeDeleteAttachmentDialog()
                    this.attachmentLoadingOnDelete = false
                })
        },
        showDeleteAttachmentDialog(attachment) {
            this.currentAttachment = attachment
            this.deleteAttachmentDialog = true
        },
        closeDeleteAttachmentDialog() {
            this.currentAttachment = {}
            this.deleteAttachmentDialog = false
        },
        removeAttachment(item) {
            this.items.splice(this.items.indexOf(item), 1)
        },
        saveProcessSEntry() {
            this.actionLoading = true
            delete this.processEntry['processEntryAttachment']
            this.$store.dispatch('wiki/updateProcessEntry', this.processEntry)
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading = false
                    this.onClose()
                })
            if (this.items.length > 0) {
                const formData = new FormData()

                this.items.forEach(item => {
                    formData.append('file', item)
                })
                formData.append('processEntryId', this.processEntry.id)
                this.$store.dispatch('wiki/createAttachment', formData)
                    .catch(error => {
                        this.closeProcessEntryDialog()
                        errorHandler(error)
                    })
            }
        },
        getAncestorsNamesPath(names) {
            const filteredNames = names.filter(name => name.id >= 0)
            const titles = filteredNames.map(function(item) {
                return item['name']
            })
            
            return titles.join(' / ')
        }
    }
}
</script>